/* eslint-disable react/prop-types */
import { useState } from 'react';
import './StyleRecommendationSections.css';

const hairSearchUrl = (name) => 'https://www.google.com/search?' + new URLSearchParams({ tbm: 'isch', q: name + ' hairstyle' }).toString();

const stars = (score) => '★'.repeat(Math.max(0, Math.min(5, Number(score) || 0)))
  + '☆'.repeat(Math.max(0, 5 - Math.min(5, Number(score) || 0)));

function Section({ eyebrow, title, children, className = '', defaultOpen = false }) {
  return (
    <details className={`style-recommendation-section ${className}`} open={defaultOpen}>
      <summary>
        <div><p className="style-recommendation-eyebrow">{eyebrow}</p><h2>{title}</h2></div>
        <span aria-hidden="true">+</span>
      </summary>
      <div className="style-recommendation-content">{children}</div>
    </details>
  );
}

export function CurrentStyleHeading({ t }) {
  return (
    <div className="style-current-heading">
      <p className="style-recommendation-eyebrow">{t('style_check.extension.current.eyebrow')}</p>
      <h2>{t('style_check.extension.current.title')}</h2>
      <span>{t('style_check.extension.current.description')}</span>
    </div>
  );
}

export default function StyleRecommendationSections({ result, faceImageSrc, t }) {
  const [comingSoon, setComingSoon] = useState('');
  const hair = result?.hair_analysis;
  const recommendations = Array.isArray(result?.recommended_hairstyles)
    ? [...result.recommended_hairstyles].sort((a, b) => (Number(b?.recommendation_score) || 0) - (Number(a?.recommendation_score) || 0)) : [];
  const avoid = Array.isArray(result?.avoid_hairstyles) ? result.avoid_hairstyles : [];
  const makeup = result?.makeup || {};
  const roadmap = Array.isArray(result?.upgrade_roadmap)
    ? [...result.upgrade_roadmap].sort((a, b) => a.priority - b.priority) : [];
  const makeupRows = ['base', 'skin', 'eyebrow', 'eye', 'lip', 'beard', 'contour']
    .filter((key) => typeof makeup[key] === 'string' && makeup[key].trim());

  return (
    <div className="style-recommendation-stack">
      <Section eyebrow={t('style_check.extension.hair.eyebrow')} title={t('style_check.extension.hair.title')} defaultOpen>
        {faceImageSrc && <figure className="style-hair-face-photo"><img src={faceImageSrc} alt={t('style_check.face_upload.preview_alt')} /></figure>}
        <div className="style-hair-analysis-grid">
          <div><strong>{t('style_check.extension.hair.features')}</strong><ul>{(hair?.current_features || []).map((item) => <li key={item}>{item}</li>)}</ul></div>
          <div><strong>{t('style_check.extension.hair.strengths')}</strong><ul>{(hair?.strengths || []).map((item) => <li key={item}>{item}</li>)}</ul></div>
          <div><strong>{t('style_check.extension.hair.opportunities')}</strong><ul>{(hair?.opportunities || []).map((item) => <li key={item}>{item}</li>)}</ul></div>
        </div>
        <div className="style-hair-summary">
          <p><strong>{t('style_check.extension.hair.harmony')}</strong>{hair?.face_harmony}</p>
          <p><strong>{t('style_check.extension.hair.mood')}</strong>{hair?.current_mood}</p>
        </div>
      </Section>

      <Section eyebrow="TOP 3" title={t('style_check.extension.recommended.title')} defaultOpen>
        <div className="style-hairstyle-grid">
          {recommendations.map((style, index) => (
            <article className="style-hairstyle-card" key={`${style.name}-${index}`}>
              <div className="style-hairstyle-card-body">
                <p className="style-hairstyle-rank">0{index + 1}</p>
                <h3>{style.name}</h3>
                <span className="style-hairstyle-stars" aria-label={`${style.recommendation_score}/5`}>
                  {stars(style.recommendation_score)}
                </span>
                <div className="style-hairstyle-comparison">
                  <div><strong>{t('style_check.extension.recommended.current')}</strong><p>{style.current_state}</p></div>
                  <span aria-hidden="true">↓</span>
                  <div><strong>{t('style_check.extension.recommended.problem')}</strong><p>{style.problem}</p></div>
                  <span aria-hidden="true">↓</span>
                  <div><strong>{t('style_check.extension.recommended.recommendation')}</strong><p>{style.recommended_change}</p></div>
                  <span aria-hidden="true">↓</span>
                  <div className="is-change"><strong>{t('style_check.extension.recommended.change')}</strong><p>{style.expected_change}</p></div>
                </div>
                <p className="style-hairstyle-reason">{style.recommendation_reason}</p>
                <p className="style-hairstyle-fit">{style.compatibility_reason}</p>
                <small>{t('style_check.extension.recommended.difficulty')}: {t(`style_check.extension.difficulty.${style.maintenance_difficulty}`, style.maintenance_difficulty)}</small>
                <a className="style-hairstyle-search" href={hairSearchUrl(style.name)} target="_blank" rel="noopener noreferrer">{t('style_check.extension.recommended.search')}</a>
              </div>
            </article>
          ))}
        </div>
      </Section>

      <Section eyebrow={t('style_check.extension.avoid.eyebrow')} title={t('style_check.extension.avoid.title')} className="style-avoid-section">
        <div className="style-avoid-grid">
          {avoid.map((style) => <article key={style.name}><h3>{style.name}</h3><p>{style.reason}</p></article>)}
        </div>
      </Section>

      <Section eyebrow={t('style_check.extension.makeup.eyebrow')} title={t('style_check.extension.makeup.title')}>
        <div className="style-makeup-grid">
          {makeupRows.map((key) => (
            <article key={key}>
              <span>{t(`style_check.extension.makeup.${key}`)}</span>
              <p>{makeup[key]}</p>
            </article>
          ))}
        </div>
        {(makeup.notes || []).map((note) => <p className="style-makeup-note" key={note}>{note}</p>)}
      </Section>

      <Section eyebrow={t('style_check.extension.roadmap.eyebrow')} title={t('style_check.extension.roadmap.title')}>
        <ol className="style-roadmap">
          {roadmap.map((step) => (
            <li key={`${step.priority}-${step.category}`}>
              <span>{step.priority}</span>
              <div>
                <small>{t(`style_check.extension.roadmap.${step.category}`)}</small>
                <h3>{step.action}</h3>
                <p>{step.reason}</p>
                <em>{step.expected_impact}</em>
              </div>
            </li>
          ))}
        </ol>
      </Section>

      <Section eyebrow="NEXT" title={t('style_check.extension.preview.title')} className="style-preview-section">
        <p>{t('style_check.extension.preview.description')}</p>
        <div>
          <button type="button" onClick={() => setComingSoon('hair')}>{t('style_check.extension.preview.hair')}</button>
          <button type="button" onClick={() => setComingSoon('makeup')}>{t('style_check.extension.preview.makeup')}</button>
        </div>
        {comingSoon && <span role="status">{t('style_check.extension.preview.coming_soon')}</span>}
      </Section>
    </div>
  );
}
